/**
 * ReceivablesCard — "Outstanding invoices", for admin and finance.
 *
 * MKS-329:
 *   - rows come from `GET /api/invoices/outstanding`, aggregated server-side
 *     by CUSTOMER, instead of the browser downloading every invoice and
 *     grouping by the invoice's first project;
 *   - a paid invoice is not outstanding, and overdue is counted from the
 *     invoice's `dueDate`, by the same derivation as the `/invoices` badges —
 *     the old card counted days from the invoice's `to` date, so a 30-day-terms
 *     invoice looked a month overdue the day it was issued;
 *   - the currency comes from the server rather than being hard-coded.
 *
 * Customer names render as JSX text via React's default escaping (T-41-14
 * mitigation — no dangerouslySetInnerHTML).
 */
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Pill } from '../ui/Pill';
import {
  useOutstandingInvoices,
  type OutstandingCustomerRow,
} from '../../hooks/useDashboardReceivables';
import { formatDisplayDate } from '../../utils/format';
import { LEGACY_EN_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

const TITLE = 'Outstanding invoices';

function fmt(n: number, ccy: string): string {
  return numberFormatter({
    style: 'currency',
    currency: ccy,
    maximumFractionDigits: 0,
  }, LEGACY_EN_NUMBER_LOCALE).format(n);
}

/**
 * MKS-329 — "15d overdue" only for a row that actually has overdue money;
 * everything else is just unpaid. The old card put every row in one of two
 * states (overdue / due in Nd) because it had no due date to tell them apart.
 */
function chip(row: OutstandingCustomerRow) {
  if (row.maxDaysOverdue > 0) {
    return (
      <Pill bg="#FFE4E6" fg="#BE123C">
        {row.maxDaysOverdue}d overdue
      </Pill>
    );
  }
  if (row.oldestDueDate) {
    return (
      <Pill bg="#E0F2FE" fg="#075985">
        due {formatDisplayDate(row.oldestDueDate)}
      </Pill>
    );
  }
  // No due date on any of this customer's invoices — every invoice issued
  // before MKS-214 is in exactly that state, and none of them is overdue.
  return (
    <Pill bg="#F1F5F9" fg="#475569">
      no due date
    </Pill>
  );
}

/**
 * MKS-334 — the card renders the top five customers, so on a real instance it
 * hides most of what is owed. The footer says how much that is in total, how
 * many rows are not shown, and links to the page that shows all of them.
 */
function ReceivablesFooter({ rows, shown }: { rows: OutstandingCustomerRow[]; shown: number }) {
  const total = rows.reduce((sum, row) => sum + row.outstandingTotal, 0);
  const hidden = rows.length - shown;
  const currency = rows[0]?.currency ?? 'CZK';
  return (
    <div className="mt-2 flex items-center justify-between border-t border-ms-border pt-2 text-[11px]">
      <Link to="/invoices/receivables" className="font-medium text-ms-text-strong hover:underline">
        All receivables{hidden > 0 ? ` · +${hidden} more` : ''}
      </Link>
      <span className="font-semibold tabular-nums text-ms-text-strong">
        {fmt(total, currency)}
      </span>
    </div>
  );
}

export function ReceivablesCard() {
  const { data, isLoading } = useOutstandingInvoices(true);
  if (isLoading || !data) {
    return (
      <Card title={TITLE}>
        <div className="text-[12px] text-ms-text-muted">Loading…</div>
      </Card>
    );
  }
  if (data.length === 0) {
    return (
      <Card title={TITLE}>
        <p className="text-[12px] text-ms-text-muted">No outstanding invoices.</p>
      </Card>
    );
  }
  const shown = data.slice(0, 5);
  return (
    <Card title={TITLE}>
      <ul className="divide-y divide-ms-border">
        {shown.map((r) => (
          <li key={r.key} className="py-2 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-ms-text-strong truncate">
                {r.customerName}
              </div>
              <div className="text-[10.5px] text-ms-text-muted">
                {r.invoiceCount} unpaid {r.invoiceCount === 1 ? 'invoice' : 'invoices'}
                {r.overdueTotal > 0 ? ` · ${fmt(r.overdueTotal, r.currency)} overdue` : ''}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[12px] font-semibold tabular-nums text-ms-text-strong">
                {fmt(r.outstandingTotal, r.currency)}
              </span>
              {chip(r)}
            </div>
          </li>
        ))}
      </ul>
      <ReceivablesFooter rows={data} shown={shown.length} />
    </Card>
  );
}
